import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { Plus, Trash2, Save, LogOut, Star, ImagePlus, RefreshCw, ExternalLink, KeyRound } from 'lucide-react';
import { listScholars, createScholar, updateScholar, deleteScholar, getToken, clearToken, me } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { iceLogo } from '../mock';

const emptyForm = { name: '', year: '', img: '', focus: 'center top', featured: false, order: 0 };

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

export default function AdminScholars() {
  const nav = useNavigate();
  const { toast } = useToast();
  const [scholars, setScholars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [authOk, setAuthOk] = useState(null);

  useEffect(() => {
    if (!getToken()) { setAuthOk(false); return; }
    me().then(() => setAuthOk(true)).catch(() => { clearToken(); setAuthOk(false); });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listScholars();
      setScholars(data.sort((a, b) => (a.order || 0) - (b.order || 0)));
    } catch (e) {
      toast({ title: 'Error loading scholars', description: e.message });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { if (authOk) load(); }, [authOk, load]);

  if (authOk === false) return <Navigate to="/admin/login" replace />;
  if (authOk === null) return null;

  const startNew = () => {
    setEditingId(null);
    setForm({ ...emptyForm, order: scholars.length });
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setForm({ name: s.name, year: s.year, img: s.img || '', focus: s.focus || 'center top', featured: !!s.featured, order: s.order || 0 });
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) {
      toast({ title: 'Photo too large', description: 'Please choose an image under 3 MB.' });
      return;
    }
    const b64 = await fileToBase64(f);
    setForm((prev) => ({ ...prev, img: b64 }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || !form.year) {
      toast({ title: 'Missing info', description: 'Name and Year/Caption are required.' });
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await updateScholar(editingId, form);
        toast({ title: 'Scholar updated' });
      } else {
        await createScholar(form);
        toast({ title: 'Scholar added' });
      }
      setEditingId(null);
      setForm(emptyForm);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast({ title: 'Save failed', description: typeof detail === 'string' ? detail : 'Try again.' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete ${name}?`)) return;
    try {
      await deleteScholar(id);
      toast({ title: 'Scholar deleted' });
      if (editingId === id) { setEditingId(null); setForm(emptyForm); }
      load();
    } catch (err) {
      toast({ title: 'Delete failed', description: err.message });
    }
  };

  const logout = () => { clearToken(); nav('/admin/login'); };

  return (
    <div className="min-h-screen bg-[#f5f6f8] font-body">
      {/* Top bar */}
      <div className="bg-[#0a1f44] text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={iceLogo} alt="ICE" className="h-10 w-auto object-contain" />
            <div>
              <div className="font-display text-xl tracking-wide">ADMIN CMS</div>
              <div className="font-heading uppercase tracking-widest text-[10px] text-[#c8102e]">Scholars Manager</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/coaches" data-testid="admin-coach-accounts-link" className="inline-flex items-center gap-2 text-sm font-heading uppercase tracking-widest text-white/70 hover:text-white"><KeyRound className="w-4 h-4" /> Coach Access</Link>
            <Link to="/scholars" target="_blank" data-testid="view-live-btn" className="hidden md:inline-flex items-center gap-2 text-sm font-heading uppercase tracking-widest text-white/70 hover:text-white">
              <ExternalLink className="w-4 h-4" /> View Live
            </Link>
            <Link to="/admin/account" data-testid="admin-nav-account" className="inline-flex items-center gap-2 text-sm font-heading uppercase tracking-widest text-white/70 hover:text-white border border-white/20 px-3 py-2"><KeyRound className="w-4 h-4" /> Change Password</Link>
            <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest text-xs px-4 py-2 transition-colors">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 grid lg:grid-cols-5 gap-6">
        {/* Form */}
        <form onSubmit={save} className="lg:col-span-2 bg-white shadow-sm p-6 space-y-4 h-fit sticky top-6" data-testid="scholar-form">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-2xl text-[#0a1f44]">{editingId ? 'EDIT SCHOLAR' : 'ADD SCHOLAR'}</h2>
            {editingId && (
              <button type="button" onClick={startNew} data-testid="new-scholar-btn" className="text-xs font-heading uppercase tracking-widest text-[#c8102e] inline-flex items-center gap-1"><Plus className="w-3 h-3" /> New</button>
            )}
          </div>

          <label className="block">
            <span className="block text-xs font-heading uppercase tracking-widest text-[#0a1f44]/70 mb-2">Photo</span>
            <div className="flex gap-3 items-start">
              <div className="w-24 h-24 bg-[#0a1f44] flex items-center justify-center overflow-hidden flex-shrink-0">
                {form.img ? (
                  <img src={form.img} alt="preview" className="w-full h-full object-cover" style={{ objectPosition: form.focus }} />
                ) : (
                  <ImagePlus className="w-8 h-8 text-white/40" />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input type="file" accept="image/*" onChange={onFile} data-testid="scholar-photo-file" className="text-xs w-full" />
                <input
                  type="text"
                  placeholder="…or paste photo URL"
                  value={form.img && !form.img.startsWith('data:') ? form.img : ''}
                  onChange={(e) => setForm({ ...form, img: e.target.value })}
                  data-testid="scholar-photo-url"
                  className="w-full text-xs border border-[#0a1f44]/20 px-2 py-1.5 focus:outline-none focus:border-[#c8102e]"
                />
              </div>
            </div>
          </label>

          <Field label="Full Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="scholar-name" required />
          <Field label="Year / Caption *" value={form.year} onChange={(v) => setForm({ ...form, year: v })} testid="scholar-year" placeholder="2025 · Class of 2028" required />
          <Field label="Photo Focus (CSS object-position)" value={form.focus} onChange={(v) => setForm({ ...form, focus: v })} testid="scholar-focus" placeholder="center top" />
          <Field label="Display Order" value={String(form.order)} onChange={(v) => setForm({ ...form, order: Number(v) || 0 })} testid="scholar-order" type="number" />

          <label className="flex items-center gap-3 cursor-pointer" data-testid="scholar-featured-label">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="scholar-featured" className="accent-[#c8102e] w-4 h-4" />
            <span className="text-sm text-[#0a1f44] font-heading tracking-wider inline-flex items-center gap-1"><Star className="w-4 h-4 text-[#c8102e]" /> Featured (ALL-AMERICAN badge)</span>
          </label>

          <button type="submit" disabled={saving} data-testid="scholar-save-btn" className="w-full bg-[#c8102e] hover:bg-[#e11d34] disabled:opacity-50 text-white font-heading uppercase tracking-widest px-6 py-3 inline-flex items-center justify-center gap-2 transition-colors">
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Add Scholar'}
          </button>
        </form>

        {/* List */}
        <div className="lg:col-span-3">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display text-2xl text-[#0a1f44]">{scholars.length} SCHOLAR{scholars.length !== 1 ? 'S' : ''}</h2>
              <div className="font-heading uppercase tracking-widest text-[10px] text-[#c8102e]">Live on the site</div>
            </div>
            <button onClick={load} className="text-xs font-heading uppercase tracking-widest text-[#0a1f44]/70 inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Reload</button>
          </div>

          {loading ? (
            <div className="text-center py-10 text-[#0a1f44]/50">Loading…</div>
          ) : scholars.length === 0 ? (
            <div className="text-center py-16 bg-white border-2 border-dashed border-[#0a1f44]/20 text-[#0a1f44]/60">
              No scholars yet. Add your first Patriot on the left.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4" data-testid="scholars-list">
              {scholars.map((s) => (
                <div key={s.id} data-testid={`scholar-row-${s.id}`} className={`bg-white border-2 ${editingId === s.id ? 'border-[#c8102e]' : 'border-[#0a1f44]/10'} overflow-hidden`}>
                  <div className="flex gap-3 p-3">
                    <div className="w-20 h-20 bg-[#0a1f44] flex-shrink-0 overflow-hidden">
                      {s.img ? (
                        <img src={s.img} alt={s.name} className="w-full h-full object-cover" style={{ objectPosition: s.focus || 'center top' }} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white/40"><ImagePlus className="w-6 h-6" /></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2">
                        <div className="font-display text-lg text-[#0a1f44] truncate flex-1">{s.name.toUpperCase()}</div>
                        {s.featured && <Star className="w-4 h-4 text-[#c8102e] fill-[#c8102e] flex-shrink-0 mt-1" />}
                      </div>
                      <div className="font-heading uppercase tracking-widest text-[10px] text-[#c8102e] mt-1 line-clamp-2">{s.year}</div>
                      <div className="mt-2 flex gap-2">
                        <button onClick={() => startEdit(s)} data-testid={`edit-${s.id}`} className="text-xs bg-[#0a1f44] hover:bg-[#0a1f44]/80 text-white font-heading uppercase tracking-widest px-3 py-1.5">Edit</button>
                        <button onClick={() => remove(s.id, s.name)} data-testid={`delete-${s.id}`} className="text-xs bg-white border border-[#c8102e] text-[#c8102e] hover:bg-[#c8102e] hover:text-white font-heading uppercase tracking-widest px-3 py-1.5 inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = 'text', placeholder = '', required = false }) {
  return (
    <label className="block">
      <span className="block text-xs font-heading uppercase tracking-widest text-[#0a1f44]/70 mb-2">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        data-testid={testid}
        className="w-full border border-[#0a1f44]/20 px-4 py-3 focus:outline-none focus:border-[#c8102e]"
      />
    </label>
  );
}
